import { useEffect, useMemo, useState } from "react";
import { Product } from "@/types/types.ts";
import { formatTimeToAmPm } from "@/utils/date/formatTime";
import { DMS_IDS } from "@/constants/constants";
import { WaitroseLogoSvg } from "../../ClickAndCollectCountdown/utils/WaitroseLogoSvg";
import type { Card } from "../ReasonToBuy";

type AvailableOption = {
  aliases?: { dmsId?: string | null } | null;
  earliestFulfilment?: {
    orderByLocalTimeToday?: string | null;
  } | null;
};

const outOfHoursClickAndCollectCard: Card = {
  id: "clickandcollect",
  title: "Collect from your nearest Waitrose",
  icon: <WaitroseLogoSvg />,
};

function buildClickAndCollectCard(
  option: AvailableOption | undefined,
  hasMounted: boolean,
): Card | null {
  if (!option) return null;

  const orderByLocalTimeToday =
    option.earliestFulfilment?.orderByLocalTimeToday;

  if (!hasMounted || !orderByLocalTimeToday) {
    return outOfHoursClickAndCollectCard;
  }

  const [h, m] = orderByLocalTimeToday.split(":").map((t) => parseInt(t, 10));
  const cutoffTime = new Date();
  cutoffTime.setHours(h || 0, m || 0, 0, 0);
  const isBeforeCutoff = cutoffTime.getTime() - Date.now() > 0;

  if (!isBeforeCutoff) {
    return outOfHoursClickAndCollectCard;
  }

  return {
    id: "clickandcollect",
    title: "Get it tomorrow",
    description: `Order before ${formatTimeToAmPm(orderByLocalTimeToday)} and collect from your nearest Waitrose`,
    icon: <WaitroseLogoSvg />,
  };
}

export function useClickAndCollectCard(product: Product): Card | null {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return useMemo(() => {
    const cncOption = product.fulfilmentOptions?.available?.find(
      (option) => option.aliases?.dmsId === DMS_IDS.ClickAndCollect,
    );
    return buildClickAndCollectCard(cncOption, hasMounted);
  }, [product, hasMounted]);
}
